import { useEffect, useState } from 'react';
import axios from 'axios';
import { authTokenStore } from '../stores/authToken.store.ts';
import { userStore } from '../stores/user.store.ts';
import { useCurrencyStore } from '../stores/currency.store.ts';
import type { CurrencyType } from '../models/booking.model.ts';
import { http, SESSION_EXPIRED_FLAG } from '../api/http.ts';
import { globalErrorStore } from '../stores/globalError.store.ts';
import { ENDPOINTS } from '../api/endpoints.ts';
import { UserDataSchema } from '../models/user.model.ts';
import { BookmarkRepository } from '../repositories/bookamrk.repository.ts';
import { NotificationRepository } from '../repositories/notification.repository.ts';

export function useSessionInit(): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const isCallbackRoute = window.location.pathname.includes('/auth/callback');

    if (isCallbackRoute) {
      authTokenStore.getState().hydrateFromStorage();
      userStore.getState().hydrateFromStorage();
      useCurrencyStore.getState().hydrateFromStorage();
      setReady(true);
      return;
    }

    async function init() {
      // 세션 만료로 리로드됐으면 그 안내를 여기서 띄운다(http.ts clearSessionAndReload).
      try {
        if (sessionStorage.getItem(SESSION_EXPIRED_FLAG)) {
          sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
          globalErrorStore.getState().setError({
            message: 'Your session has expired. Please sign in again.',
            statusCode: 401,
            type: 'snackBar',
          });
        }
      } catch {}

      authTokenStore.getState().hydrateFromStorage();
      userStore.getState().hydrateFromStorage();
      useCurrencyStore.getState().hydrateFromStorage();

      const accessToken = authTokenStore.getState().accessToken;

      if (!accessToken) {
        setReady(true);
        return;
      }

      // access token이 있지만 user가 없으면 프로필 API로 복구
      const currentUser = userStore.getState().user;
      if (!currentUser) {
        try {
          const res = await http.get(ENDPOINTS.user.updateUserInfo);
          const data = (res.data as any)?.data ?? res.data;
          const parsed = UserDataSchema.parse(data);
          userStore.getState().setUser(parsed);
        } catch (e) {
          // 진짜 인증 실패(401)일 때만 토큰 정리 → 재로그인 유도.
          // 일시적 장애(500/타임아웃/네트워크)에는 토큰을 유지하고 그대로
          // 진행한다 — 백엔드 순간 오류로 조용히 로그아웃되던 문제 방지.
          const status = axios.isAxiosError(e) ? e.response?.status : undefined;
          if (status === 401) {
            authTokenStore.getState().clearTokens();
            userStore.getState().clearUser();
          }
        }
      }

      // 로그인 사용자의 currency 를 currency store 에 동기화
      const loggedInUser = userStore.getState().user;
      if (loggedInUser?.currency) {
        useCurrencyStore.getState().setCurrency(loggedInUser.currency as CurrencyType);
      }

      // 로그인된 유저의 북마크 UUID 동기화 + 읽지 않은 알림 체크 (fire-and-forget)
      const finalUser = userStore.getState().user;
      if (finalUser) {
        // fire-and-forget — 실패해도 부팅을 막지 않도록 rejection 무해화
        Promise.resolve(BookmarkRepository.syncBookmarks()).catch(() => {});
        Promise.resolve(NotificationRepository.checkNewNotifications()).catch(() => {});
      }

      setReady(true);
    }

    init();
  }, []);

  return ready;
}
